import React, { useState, useEffect, useMemo } from 'react';
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  FileText, 
  Settings as SettingsIcon, 
  Plus, 
  Search, 
  Trash2, 
  Save, 
  X, 
  Mail, 
  Phone, 
  DollarSign, 
  ChevronLeft, 
  Filter, 
  Upload, 
  Edit, 
  Download, 
  Database, 
  RefreshCw, 
  Globe, 
  User, 
  CheckCircle, 
  Send, 
  ArrowLeft, 
  ArrowRight,
  BarChart3, 
  LogOut, 
  Shield, 
  Cloud, 
  FileSpreadsheet, 
  Loader, 
  Copy, 
  Sun, 
  Moon, 
  MessageCircle,
  FileX,
  Bell, 
  Clock,
  AlertCircle,
  Check,
  MapPin,
  Hash,
  UserCheck,
  Smartphone,
  Tag
} from 'lucide-react';

import BackgroundOrbs from './components/BackgroundOrbs';
import Card from './components/Card';
import Button from './components/Button';
import Badge from './components/Badge';
import Modal from './components/Modal';
import LoginView from './pages/LoginView';
import CurrencyView from './pages/CurrencyView';
import CustomersView from './pages/CustomersView';
import ProductsView from './pages/ProductsView';
import AppSettings from './pages/AppSettings';
import DashboardView from './pages/DashboardView';
import QuotationBuilder from './pages/QuotationBuilder';
import AnalyticsView from './pages/AnalyticsView';
import AlertsView from './pages/AlertsView';
import { THEMES, INITIAL_USERS, INITIAL_PRODUCTS, INITIAL_CUSTOMERS, INITIAL_QUOTES, DEFAULT_CATEGORIES, DEFAULT_RATES, DEFAULT_BANK_DETAILS, DEFAULT_TERMS } from './config/constants';
import { apiCall, setAuthToken } from './services/api';

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [theme, setTheme] = useState('light');
  const [isScriptsLoaded, setIsScriptsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  
  const [users, setUsers] = useState(INITIAL_USERS);
  const [products, setProducts] = useState(INITIAL_PRODUCTS);
  const [customers, setCustomers] = useState(INITIAL_CUSTOMERS);
  const [quotes, setQuotes] = useState(INITIAL_QUOTES);
  const [loginLogs, setLoginLogs] = useState([]);
  
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [exchangeRates, setExchangeRates] = useState(DEFAULT_RATES);
  const [bankDetails, setBankDetails] = useState(DEFAULT_BANK_DETAILS);
  const [terms, setTerms] = useState(DEFAULT_TERMS.text);
  
  const [quoteViewMode, setQuoteViewMode] = useState('list');
  const [activeQuoteId, setActiveQuoteId] = useState(null);
  const [draftQuote, setDraftQuote] = useState(null);
  const [followUpFilter, setFollowUpFilter] = useState(null);
  const [priceAlerts, setPriceAlerts] = useState([]);

  useEffect(() => {
    if (activeTab !== 'quotes') {
        setQuoteViewMode('list');
        setActiveQuoteId(null);
    }
  }, [activeTab]);

  useEffect(() => {
    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            if(document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
        });
    };
    Promise.all([
        loadScript("https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js"),
        loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js")
    ]).then(() => { setIsScriptsLoaded(true); }).catch(err => console.error(err));
  }, []);

  const processDataPayload = (data) => {
    setIsConnected(true);
    if (data.current_user) setCurrentUser(data.current_user);
    if (data.users) setUsers(data.users);
    if (data.products) setProducts(data.products);
    if (data.customers) setCustomers(data.customers);
    if (data.quotes) setQuotes(data.quotes);
    if (data.settings) {
      if (data.settings.categories) setCategories(data.settings.categories);
      if (data.settings.exchange_rates) setExchangeRates(data.settings.exchange_rates);
      if (data.settings.bank_details) setBankDetails(data.settings.bank_details);
      if (data.settings.global_terms) {
        let loadedTerms = data.settings.global_terms;
        try {
          const parsed = JSON.parse(loadedTerms);
          if (!Array.isArray(parsed)) throw new Error();
        } catch(e) {
          loadedTerms = JSON.stringify([{ id: 'default', name: 'System Default', content: loadedTerms }]);
        }
        setTerms(loadedTerms);
      }
      
      if (data.settings.login_logs) setLoginLogs(data.settings.login_logs);
      if (data.settings.price_alerts) setPriceAlerts(data.settings.price_alerts);
    }
    
    // Fetch product images in background to keep initial load instant
    apiCall('get_product_images').then(imagesData => {
      if (imagesData && Array.isArray(imagesData)) {
        setProducts(prevProducts => prevProducts.map(p => {
          const imgObj = imagesData.find(img => img.id === p.id);
          return imgObj ? { ...p, image: imgObj.image } : p;
        }));
      }
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    const data = await apiCall('get_all_data');
    if (data && data.users) {
      processDataPayload(data);
    } else {
      if (data && data.message === 'Unauthorized') {
        setIsConnected(true); // Connected to DB, just not authorized
      } else {
        setIsConnected(false);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateNewQuote = () => {
      const creator = users.find(u => u.id === currentUser.id);
      let userTerms = terms;
      if (creator?.terms) {
          try {
              const parsed = JSON.parse(creator.terms);
              if (Array.isArray(parsed) && parsed.length > 0) userTerms = parsed[0].content;
              else userTerms = creator.terms;
          } catch(e) { userTerms = creator.terms; }
      } else {
          try {
              const parsed = JSON.parse(terms);
              if (Array.isArray(parsed) && parsed.length > 0) userTerms = parsed[0].content;
          } catch(e) {}
      }
      let maxId = 1000;
      quotes.forEach(q => {
          const match = (q.id || "").match(/^(?:QT|PI)-(\d+)/);
          if (match) {
              const num = parseInt(match[1], 10);
              if (num > maxId) maxId = num;
          }
      });
      const newId = `QT-${maxId + 1}`;
      setDraftQuote({ id: newId, customerId: '', userId: currentUser.id, date: new Date().toISOString().split('T')[0], items: [], subtotal: 0, tax: 0, total: 0, status: 'Draft', currency: currentUser.region==='INTERNATIONAL'?'USD':'INR', terms: userTerms, transportation: 0, documentType: 'QUOTATION', exchangeRates: currentUser?.exchangeRates || exchangeRates });
      setQuoteViewMode('create');
      setActiveTab('quotes');
  };

  const handleLogin = async (data) => {
      setAuthToken(data.token);
      processDataPayload(data);
      setActiveTab('dashboard');
      if (data.current_user) {
          const newLog = { userId: data.current_user.id, username: data.current_user.username, timestamp: new Date().toISOString() };
          const updatedLogs = [...loginLogs, newLog];
          setLoginLogs(updatedLogs);
          await apiCall('save_setting', { key: 'login_logs', value: updatedLogs }).catch(console.error);
      }
  };

  const handleLogout = () => {
      setAuthToken(null);
      setCurrentUser(null);
      setActiveTab('dashboard');
      setQuoteViewMode('list');
      setActiveQuoteId(null);
      setDraftQuote(null);
      setFollowUpFilter(null);
  };

  const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

  if (!currentUser) return <LoginView onLogin={handleLogin} users={users} isLoading={isLoading} isConnected={isConnected} theme={theme} />;

  const NAV_ITEMS = [
      {id:'dashboard', icon: LayoutDashboard, label: 'Dashboard'},
      {id:'quotes', icon: FileText, label: 'Quotations'},
      {id:'analytics', icon: BarChart3, label: 'Analytics'},
      {id:'customers', icon: Users, label: 'Customers'},
      {id:'products', icon: Package, label: 'Products'},
      {id:'currency', icon: DollarSign, label: 'Currency'},
      {id:'settings', icon: SettingsIcon, label: 'Settings', adminOnly: true},
      {id:'alerts', icon: Bell, label: 'Alerts', adminOnly: true},
  ];

  return (
    <div className={`flex h-screen ${THEMES[theme].bg} font-sans transition-colors duration-500 overflow-hidden`}>
      <BackgroundOrbs />
      <aside className={`w-72 ${THEMES[theme].sidebar} flex flex-col h-full z-40 transition-all duration-300 ease-in-out`}>
        <div className="p-8 flex flex-col items-center justify-center gap-4 h-32">
             <img src="./logo-1.png" alt="Onyx Logo" className="h-16 w-auto object-contain drop-shadow-md" />
             <span className="block font-black text-2xl tracking-tighter text-slate-800 text-center whitespace-nowrap">Onyx<span className="text-blue-600">CRM</span></span>
        </div>
        <nav className="py-4 space-y-3 flex-1 flex flex-col px-4">
            {NAV_ITEMS.filter(i=>!i.adminOnly||currentUser.role==='admin').map(i=>(
                <button key={i.id} onClick={()=>{setActiveTab(i.id); if(i.id === 'quotes') setFollowUpFilter(null);}} className={`w-full flex items-center justify-start gap-4 p-4 rounded-[1.2rem] transition-all duration-300 ${activeTab===i.id ? THEMES[theme].sidebarActive : `${THEMES[theme].sidebarText} hover:bg-white/40`}`}>
                    <i.icon size={22} /><span className="block font-medium">{i.label}</span>
                </button>
            ))}
        </nav>
        <div className="p-6 flex flex-col items-center gap-4 mt-auto">
            <div className="flex w-full gap-2">
                <button onClick={toggleTheme} className={`flex-1 p-3 rounded-2xl transition-all flex items-center justify-center ${theme==='dark' ? 'bg-slate-800 text-yellow-400' : 'bg-orange-100/50 text-orange-500 hover:bg-orange-200/50'}`}>
                    {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                </button>
                <button onClick={handleLogout} className="flex-1 p-3 rounded-2xl bg-slate-100/50 text-slate-500 hover:text-red-100/50 hover:text-red-500 transition-colors flex items-center justify-center">
                    <LogOut size={20}/>
                </button>
            </div>
            <div className={`flex items-center gap-3 w-full p-3 rounded-2xl bg-white/40 border border-white/50 justify-start`}>
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold shrink-0">{currentUser.name.charAt(0)}</div>
                <div className="block overflow-hidden"><div className="text-sm font-bold truncate text-slate-800">{currentUser.name}</div><div className="text-xs text-slate-500 truncate">{currentUser.role}</div></div>
            </div>
        </div>
      </aside>
      <main className="flex-1 overflow-auto w-full relative custom-scrollbar">
        {activeTab === 'dashboard' && <DashboardView quotes={quotes} customers={customers} setActiveTab={setActiveTab} onQuoteClick={(id)=>{setActiveQuoteId(id); setQuoteViewMode('view'); setActiveTab('quotes');}} currentUser={currentUser} onCreateNew={handleCreateNewQuote} theme={theme} users={users} onFollowUpClick={(list)=>{setFollowUpFilter(list.map(q=>q.id)); setQuoteViewMode('list'); setActiveTab('quotes');}} />}
        {activeTab === 'analytics' && <AnalyticsView quotes={quotes} customers={customers} products={products} theme={theme} currentUser={currentUser} users={users} />}
        {activeTab === 'quotes' && <QuotationBuilder quotes={quotes} setQuotes={setQuotes} customers={customers} products={products} categories={categories} viewMode={quoteViewMode} setViewMode={setQuoteViewMode} activeQuoteId={activeQuoteId} setActiveQuoteId={setActiveQuoteId} currentUser={currentUser} draftQuote={draftQuote} setDraftQuote={setDraftQuote} onCreateNew={handleCreateNewQuote} exchangeRates={exchangeRates} bankDetails={bankDetails} globalTerms={terms} isScriptsLoaded={isScriptsLoaded} theme={theme} users={users} followUpFilter={followUpFilter} onClearFollowUpFilter={() => setFollowUpFilter(null)} />}
        {activeTab === 'customers' && <CustomersView customers={customers} setCustomers={setCustomers} currentUser={currentUser} users={users} isScriptsLoaded={isScriptsLoaded} theme={theme} />}
        {activeTab === 'products' && <ProductsView products={products} setProducts={setProducts} categories={categories} setCategories={setCategories} currentUser={currentUser} theme={theme} exchangeRates={exchangeRates} />}
        {activeTab === 'currency' && <CurrencyView exchangeRates={exchangeRates} setExchangeRates={setExchangeRates} currentUser={currentUser} theme={theme} users={users} setUsers={setUsers} />}
        {activeTab === 'settings' && currentUser.role === 'admin' && <AppSettings products={products} customers={customers} quotes={quotes} users={users} categories={categories} setUsers={setUsers} currentUser={currentUser} bankDetails={bankDetails} setBankDetails={setBankDetails} terms={terms} setTerms={setTerms} setProducts={setProducts} setCustomers={setCustomers} setQuotes={setQuotes} setCategories={setCategories} isScriptsLoaded={isScriptsLoaded} theme={theme} loginLogs={loginLogs} />}
        {activeTab === 'alerts' && currentUser.role === 'admin' && (
            <AlertsView 
                alerts={priceAlerts} 
                currentUser={currentUser} 
                theme={theme} 
                onClearAlerts={() => { 
                    const updated = priceAlerts.map(a => (!(a.clearedBy || []).includes(currentUser.id) ? { ...a, clearedBy: [...(a.clearedBy || []), currentUser.id] } : a)); 
                    setPriceAlerts(updated); 
                    apiCall('save_setting', { key: 'price_alerts', value: updated }); 
                }} 
            />
        )}
      </main>
    </div>
  );
}